/**
 * Claims module DTOs (§11, §15.2): one-time token exchange, current claim, Mode A/B catalogue, idempotent
 * hold and submit, status timeline, and the admin verification / request-info / approve / reject actions.
 */
import { z } from 'zod';
import { CountryCode, E164Phone, HexColor, IsoDateTime, SocialHandle, Ulid } from './common.js';
import { ClaimStatus, FulfillmentStatus, FulfillmentType, HoldStatus, PrizeTier, SelectionMode, SocialPlatform, VerificationDecision, WinnerContactStatus, } from './enums.js';
import { ClaimToken } from './ids.js';
import { ClaimField } from './lottery-config.js';
import { MoneyDto } from './money.js';
// --- session ---------------------------------------------------------------------------------------------
export const ClaimSessionParams = z.object({ token: ClaimToken });
export const ClaimSessionResponse = z.object({
    winId: z.string(),
    claimId: Ulid,
    /** The HttpOnly cookie is set by the response; this echoes its expiry for the UI. */
    sessionExpiresAt: IsoDateTime,
    status: ClaimStatus,
});
// --- catalogue (storefront, §6) -------------------------------------------------------------------------
export const OptionAxisDto = z.object({
    axisId: z.string().max(32),
    name: z.string(),
    values: z.array(z.object({ valueId: z.string().max(32), label: z.string(), swatch: HexColor.optional() })),
});
export const VariantDto = z.object({
    variantId: Ulid,
    sku: z.string(),
    options: z.record(z.string(), z.string()),
    value: MoneyDto,
    specifications: z.record(z.string(), z.string()),
    images: z.array(z.object({
        url: z.string(),
        alt: z.string(),
        width: z.number().int().optional(),
        height: z.number().int().optional(),
    })),
    /** Unavailable combinations are disabled with a reason, never returned as errors (§6.3). */
    available: z.boolean(),
    unavailableReason: z.string().optional(),
});
export const ProductDto = z.object({
    productId: Ulid,
    brandId: Ulid,
    brandName: z.string(),
    name: z.string(),
    description: z.string(),
    category: z.string(),
    heroImage: z.object({ url: z.string(), alt: z.string() }),
    gallery: z.array(z.object({ url: z.string(), alt: z.string() })),
    video: z.object({ url: z.string(), posterUrl: z.string().optional() }).optional(),
    spin360: z.array(z.string()).optional(),
    specifications: z.record(z.string(), z.string()),
    inTheBox: z.array(z.string()),
    optionAxes: z.array(OptionAxisDto),
    variants: z.array(VariantDto),
    /** Lowest variant value (Mode B comparison). */
    fromValue: MoneyDto,
});
export const ClaimCatalogDto = z.discriminatedUnion('selectionMode', [
    z.object({ selectionMode: z.literal('FIXED_PRODUCT'), product: ProductDto }),
    z.object({
        selectionMode: z.literal('VALUE_CAP'),
        valueCap: MoneyDto,
        brands: z.array(z.object({ brandId: Ulid, name: z.string(), logoUrl: z.string().optional() })),
        products: z.array(ProductDto),
    }),
]);
// --- hold ----------------------------------------------------------------------------------------------
export const CreateHoldRequest = z.object({ variantId: Ulid });
export const HoldDto = z.object({
    holdId: Ulid,
    claimId: Ulid,
    variantId: Ulid,
    status: HoldStatus,
    startedAt: IsoDateTime,
    expiresAt: IsoDateTime,
    extensionsUsed: z.number().int(),
    extensionsAllowed: z.number().int(),
    serverNow: IsoDateTime,
    variantSummary: z.object({
        productName: z.string(),
        options: z.record(z.string(), z.string()),
        value: MoneyDto,
        sku: z.string(),
    }),
    /** Explicit name for `variantId`: the (possibly materialised, D-1601) `PrizeVariant` row the hold reserves. */
    prizeVariantId: Ulid.optional(),
    /**
     * Mode B (E2E-07, D-2305): the catalogue identifiers the storefront rendered — `GET /claims/current/catalog`
     * uses the product-feed item id as both `productId` and the variant's `variantId` — so the client can mark the
     * chosen item after a refetch. `null` for a Mode A SKU. Always present in API responses.
     */
    catalogue: z
        .object({ feedItemId: Ulid, productId: Ulid, variantId: Ulid, externalSku: z.string() })
        .nullable()
        .optional(),
});
// --- submit --------------------------------------------------------------------------------------------
export const GovernmentIdType = z.enum(['NATIONAL_ID', 'PASSPORT', 'DRIVERS_LICENCE', 'VOTER_CARD', 'OTHER']);
export const IdentityInput = z.object({
    legalName: z.string().trim().min(2).max(140),
    idType: GovernmentIdType,
    /** Stored encrypted; masked everywhere else. */
    idNumber: z.string().trim().min(3).max(40),
    /** Storage reference from the signed-upload flow (§15.1); never a data URL. */
    idDocumentRef: z.string().min(1).max(512),
    idDocumentBackRef: z.string().max(512).optional(),
});
export const DeliveryAddressInput = z.object({
    line1: z.string().min(1).max(200),
    line2: z.string().max(200).optional(),
    city: z.string().min(1).max(100),
    region: z.string().max(100).optional(),
    postalCode: z.string().max(20).optional(),
    country: CountryCode,
});
export const PayoutDestinationInput = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('BANK'),
        bankCode: z.string().max(20),
        accountNumber: z.string().min(4).max(34),
        accountName: z.string().max(140),
    }),
    z.object({ kind: z.literal('WALLET'), provider: z.string().max(40), walletId: z.string().min(3).max(64) }),
]);
export const FulfillmentDetailsInput = z.discriminatedUnion('fulfillmentType', [
    z.object({
        fulfillmentType: z.literal('CASH'),
        payoutDestination: PayoutDestinationInput,
        taxFields: z.record(z.string().max(40), z.string().max(120)).optional(),
        contact: z.discriminatedUnion('kind', [
            z.object({ kind: z.literal('WHATSAPP'), value: E164Phone }),
            z.object({ kind: z.literal('SOCIAL_HANDLE'), platform: SocialPlatform, value: SocialHandle }),
        ]),
    }),
    z.object({
        fulfillmentType: z.literal('PHYSICAL'),
        email: z.email(),
        delivery: z.discriminatedUnion('kind', [
            z.object({ kind: z.literal('ADDRESS'), address: DeliveryAddressInput }),
            z.object({ kind: z.literal('PICKUP'), pickupLocationId: Ulid }),
        ]),
        contact: z.discriminatedUnion('kind', [
            z.object({ kind: z.literal('WHATSAPP'), value: E164Phone }),
            z.object({ kind: z.literal('SOCIAL_HANDLE'), platform: SocialPlatform, value: SocialHandle }),
        ]),
    }),
    z.object({
        fulfillmentType: z.literal('DIGITAL'),
        destination: z.discriminatedUnion('kind', [
            z.object({ kind: z.literal('WHATSAPP'), value: E164Phone }),
            z.object({ kind: z.literal('SOCIAL_HANDLE'), platform: SocialPlatform, value: SocialHandle }),
            z.object({ kind: z.literal('EMAIL'), value: z.email() }),
        ]),
    }),
    z.object({
        fulfillmentType: z.literal('IN_PERSON'),
        appointmentSlotId: Ulid,
        locationId: Ulid,
        additionalDocumentRefs: z.array(z.string().max(512)).default([]),
        contact: z.discriminatedUnion('kind', [
            z.object({ kind: z.literal('WHATSAPP'), value: E164Phone }),
            z.object({ kind: z.literal('SOCIAL_HANDLE'), platform: SocialPlatform, value: SocialHandle }),
        ]),
    }),
]);
export const SubmitClaimRequest = z.object({
    identity: IdentityInput.optional(),
    fulfillment: FulfillmentDetailsInput,
    /** Direct arrivals choose a follow-up platform at submission (§12.1). */
    followUpPlatform: SocialPlatform.optional(),
    declarationAccepted: z.literal(true),
    declarationVersion: z.string().min(1).max(32),
});
// --- status --------------------------------------------------------------------------------------------
export const ClaimTimelineEntry = z.object({
    status: ClaimStatus,
    at: IsoDateTime,
    /** Participant-safe copy for this step (Appendix A). */
    text: z.string(),
    owner: z.string().optional(),
});
export const CurrentClaimDto = z.object({
    claimId: Ulid,
    winId: z.string(),
    winIdDisplay: z.string(),
    status: ClaimStatus,
    lottery: z.object({ slug: z.string(), title: z.string(), timezone: z.string(), organizerName: z.string() }),
    prize: z.object({
        prizeId: Ulid,
        name: z.string(),
        tier: PrizeTier,
        value: MoneyDto,
        fulfillmentType: FulfillmentType,
        selectionMode: SelectionMode,
        sponsorName: z.string().optional(),
        accentColor: HexColor.optional(),
        heroImageUrl: z.string().optional(),
        deliveryRegions: z.array(CountryCode),
    }),
    claimDeadline: IsoDateTime,
    requiredFields: z.array(ClaimField),
    /** Why each field is required (§11.1 step 3). */
    fieldReasons: z.record(z.string(), z.string()),
    hold: HoldDto.nullable(),
    selectedVariant: z
        .object({
        variantId: Ulid,
        productName: z.string(),
        options: z.record(z.string(), z.string()),
        value: MoneyDto,
    })
        .nullable(),
    /** Saved form data for resume (never the ID number or document). */
    savedFields: z.record(z.string(), z.string()).default({}),
    platforms: z.array(z.object({ platform: SocialPlatform, label: z.string(), accountUrl: z.string() })),
    arrivedFromPlatform: SocialPlatform.nullable(),
    serverNow: IsoDateTime,
    /**
     * §11.2 "valid data preserved" / §13.5 "not requested again" (E2E-09, D-2306): the fulfilment and contact
     * details already submitted, returned while the claim can be resubmitted (`MORE_INFO`) so the form re-hydrates
     * without asking again. A CASH payout destination comes back masked (`accountNumber` / `walletId` as
     * `•••• 6789`); submitting that masked value unchanged keeps the stored destination. `null` before the first
     * submission and in every other status.
     */
    submittedFulfillment: FulfillmentDetailsInput.nullable().optional(),
});
export const ClaimStatusDto = z.object({
    claimId: Ulid,
    winId: z.string(),
    winIdDisplay: z.string(),
    status: ClaimStatus,
    statusCopy: z.object({ key: z.string(), text: z.string(), nextAction: z.string() }),
    timeline: z.array(ClaimTimelineEntry),
    owner: z.string().nullable(),
    lastUpdateAt: IsoDateTime,
    nextMilestone: z.object({ label: z.string(), dueAt: IsoDateTime.optional() }).nullable(),
    claimDeadline: IsoDateTime,
    appealDeadline: IsoDateTime.nullable(),
    /** More-information: precisely the items to replace (§11.2). */
    requestedItems: z.array(z.object({ field: ClaimField, reason: z.string() })).default([]),
    resubmissionDeadline: IsoDateTime.nullable(),
    fulfillment: z
        .object({
        status: FulfillmentStatus,
        carrier: z.string().optional(),
        trackingRef: z.string().optional(),
        maskedDestination: z.string().optional(),
        expectedBy: IsoDateTime.optional(),
    })
        .nullable(),
    contact: z.object({
        status: WinnerContactStatus,
        platform: SocialPlatform.nullable(),
        messageUrl: z.string().nullable(),
        prefilledMessage: z.string().nullable(),
    }),
    reminders: z.array(z.object({ text: z.string(), at: IsoDateTime })),
    supportRoute: z.string(),
    serverNow: IsoDateTime,
});
// --- admin ---------------------------------------------------------------------------------------------
export const RecordVerificationRequest = z.object({
    decision: VerificationDecision,
    /** Provider reference or signed-export batch id (D08). */
    providerReference: z.string().min(1).max(128),
    reasonCodes: z.array(z.string().max(64)).default([]),
    manualFallback: z.boolean().default(false),
    note: z.string().max(2000).optional(),
});
export const RequestInfoRequest = z.object({
    items: z.array(z.object({ field: ClaimField, reason: z.string().min(3).max(300) })).min(1),
    resubmissionDeadline: IsoDateTime,
});
export const ApproveClaimRequest = z.object({
    note: z.string().max(2000).optional(),
    /** Second approver for high-value prizes (§11.2). */
    secondApproverId: Ulid.optional(),
});
export const RejectClaimRequest = z.object({
    reasonCode: z.string().min(1).max(64),
    /** Participant-safe explanation (§11.2) — rendered through copy.claim_rejected. */
    safeReason: z.string().min(3).max(300),
    internalNote: z.string().max(2000).optional(),
    evidenceRef: z.string().max(512).optional(),
});
export const ClaimAdminRowDto = z.object({
    claimId: Ulid,
    winId: z.string(),
    lotteryId: Ulid,
    status: ClaimStatus,
    prizeName: z.string(),
    prizeValue: MoneyDto,
    fulfillmentType: FulfillmentType,
    verification: z.object({
        decision: VerificationDecision.nullable(),
        providerReference: z.string().nullable(),
        decidedAt: IsoDateTime.nullable(),
    }),
    legalNameMasked: z.string().nullable(),
    claimDeadline: IsoDateTime,
    submittedAt: IsoDateTime.nullable(),
    assignedTo: Ulid.nullable(),
    requiresDualApproval: z.boolean(),
    updatedAt: IsoDateTime,
});
export const ClaimListQuery = z.object({
    lotteryId: Ulid.optional(),
    status: ClaimStatus.optional(),
    fulfillmentType: FulfillmentType.optional(),
    assignedTo: Ulid.optional(),
    sort: z.enum(['deadline', 'submittedAt', 'value']).default('deadline'),
});
// =========================================================================================================
// Claims module extensions (claims agent; append-only). Evidence upload sessions, draft/resume, hold
// extension, appeal, admin detail/queue, payout destination change, legal hold, evidence access, fulfillment
// console (§11, §15.1, §16.2, §17.3, Appendix A).
// =========================================================================================================
export const EvidenceUploadKind = z.enum([
    'ID_DOCUMENT_FRONT',
    'ID_DOCUMENT_BACK',
    'ADDITIONAL_DOCUMENT',
    'ADDRESS_PROOF',
]);
/** Accepted evidence content types (§17.1 malicious uploads: SVG never, PDF + raster images only). */
export const EVIDENCE_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
export const EVIDENCE_MAX_BYTES = 10 * 1024 * 1024;
export const PresignEvidenceRequest = z.object({
    kind: EvidenceUploadKind,
    contentType: z.enum(EVIDENCE_CONTENT_TYPES),
    sizeBytes: z.number().int().min(1).max(EVIDENCE_MAX_BYTES),
});
export const PresignEvidenceResponse = z.object({
    uploadId: Ulid,
    url: z.string(),
    method: z.literal('PUT'),
    headers: z.record(z.string(), z.string()),
    expiresAt: IsoDateTime,
    maxBytes: z.number().int(),
});
export const FinalizeEvidenceRequest = z.object({ uploadId: Ulid });
export const EvidenceUploadState = z.enum(['PENDING', 'CLEAN', 'REJECTED', 'FAILED']);
export const FinalizeEvidenceResponse = z.object({
    uploadId: Ulid,
    kind: EvidenceUploadKind,
    state: EvidenceUploadState,
    /** Opaque reference to pass as `identity.idDocumentRef` (never a URL or storage key). */
    ref: z.string().nullable(),
    /** Participant-safe reason when rejected / failed. */
    reason: z.string().nullable(),
    sizeBytes: z.number().int().nullable(),
});
/** Non-sensitive fields the claim form may park server-side for resume (§4.1 "Resume"). */
export const CLAIM_DRAFT_FIELDS = [
    'idType',
    'email',
    'deliveryKind',
    'addressLine1',
    'addressLine2',
    'city',
    'region',
    'postalCode',
    'country',
    'pickupLocationId',
    'contactKind',
    'contactPlatform',
    'destinationKind',
    'payoutKind',
    'bankCode',
    'accountName',
    'walletProvider',
    'followUpPlatform',
    'step',
];
export const ClaimDraftField = z.enum(CLAIM_DRAFT_FIELDS);
export const SaveClaimDraftRequest = z.object({
    fields: z.record(ClaimDraftField, z.string().max(200)),
});
export const ClaimAppealRequest = z.object({ message: z.string().trim().min(10).max(2000) });
export const ExtendHoldRequest = z.object({}).default({});
// --- admin: queue, detail, actions ----------------------------------------------------------------------
export const ClaimRiskFlag = z.enum([
    'NAME_MISMATCH',
    'DUPLICATE_ID_NUMBER',
    'DUPLICATE_PAYOUT_DESTINATION',
    'PROVIDER_OUTAGE',
    'HIGH_VALUE',
    'APPEAL_OPEN',
]);
export const ClaimQueueQuery = ClaimListQuery.extend({
    cursor: z.string().max(256).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    /** Only claims past their deadline or with an overdue review (§16.2 "age, deadline"). */
    overdue: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .optional(),
    riskFlag: ClaimRiskFlag.optional(),
});
export const ClaimQueueRowDto = ClaimAdminRowDto.extend({
    winIdDisplay: z.string(),
    lotteryTitle: z.string(),
    /** Age since submission (or opening) in seconds; the console renders it relative. */
    ageSeconds: z.number().int(),
    openedAt: IsoDateTime,
    /** Items the winner still owes (MORE_INFO) — precise, never "more information". */
    missingItems: z.array(z.object({ field: ClaimField, reason: z.string() })),
    riskFlags: z.array(ClaimRiskFlag),
    holdExpiresAt: IsoDateTime.nullable(),
    appealDeadline: IsoDateTime.nullable(),
    appealOpen: z.boolean(),
    awaitingSecondApproval: z.boolean(),
    fulfillmentStatus: FulfillmentStatus.nullable(),
    legalHold: z.boolean(),
});
export const ClaimEvidenceAdminDto = z.object({
    evidenceId: Ulid,
    kind: z.string(),
    mimeType: z.string().nullable(),
    sizeBytes: z.number().int().nullable(),
    uploadedAt: IsoDateTime,
    retentionClass: z.string(),
    purgeAt: IsoDateTime.nullable(),
    purged: z.boolean(),
    superseded: z.boolean(),
    /** Masked identity fields for `legal_identity` rows; documents expose no content here. */
    legalNameMasked: z.string().nullable(),
    idType: z.string().nullable(),
    idNumberMasked: z.string().nullable(),
});
export const VerificationCaseAdminDto = z.object({
    caseId: Ulid,
    provider: z.string(),
    providerReference: z.string().nullable(),
    decision: VerificationDecision,
    reasonCodes: z.array(z.string()),
    missingItems: z.array(z.string()),
    assuranceLevel: z.string().nullable(),
    submittedAt: IsoDateTime,
    decidedAt: IsoDateTime.nullable(),
    resubmitBy: IsoDateTime.nullable(),
    reviewerId: Ulid.nullable(),
    attempt: z.number().int(),
    manualFallback: z.boolean(),
});
export const FulfillmentAdminDto = z.object({
    fulfillmentId: Ulid,
    claimId: Ulid,
    winId: z.string(),
    winIdDisplay: z.string(),
    type: FulfillmentType,
    status: FulfillmentStatus,
    owner: z.string(),
    backupOwner: z.string().nullable(),
    prizeName: z.string(),
    sku: z.string().nullable(),
    /** Region only (country / city), never the street address (§16.2 "address region masked"). */
    destinationRegion: z.string().nullable(),
    destinationSummary: z.string().nullable(),
    carrier: z.string().nullable(),
    trackingRef: z.string().nullable(),
    slaDueAt: IsoDateTime.nullable(),
    overdue: z.boolean(),
    readyAt: IsoDateTime.nullable(),
    startedAt: IsoDateTime.nullable(),
    completedAt: IsoDateTime.nullable(),
    exception: z
        .object({
        stage: z.string(),
        note: z.string(),
        since: IsoDateTime,
        ageSeconds: z.number().int(),
        owner: z.string(),
        nextUpdateAt: IsoDateTime.nullable(),
    })
        .nullable(),
    evidenceRef: z.string().nullable(),
    completionRef: z.string().nullable(),
    updatedAt: IsoDateTime,
});
export const ClaimAdminDetailDto = ClaimQueueRowDto.extend({
    participantId: Ulid,
    selectedVariant: z
        .object({ variantId: Ulid, sku: z.string(), options: z.record(z.string(), z.string()), value: MoneyDto })
        .nullable(),
    hold: HoldDto.nullable(),
    chosenPlatform: SocialPlatform.nullable(),
    preferenceSummary: z.string().nullable(),
    /** Masked payout destination (CASH) — unmask through PAYOUT_DESTINATION with purpose. */
    payoutDestination: z
        .object({ payoutId: Ulid, kind: z.string(), masked: z.string(), verifiedAt: IsoDateTime.nullable() })
        .nullable(),
    verificationCase: VerificationCaseAdminDto.nullable(),
    evidence: z.array(ClaimEvidenceAdminDto),
    fulfillment: FulfillmentAdminDto.nullable(),
    timeline: z.array(ClaimTimelineEntry),
    statusReason: z.string().nullable(),
    reviewNote: z.string().nullable(),
    appeal: z
        .object({
        at: IsoDateTime,
        note: z.string(),
        decision: z.string().nullable(),
        decidedAt: IsoDateTime.nullable(),
    })
        .nullable(),
    approval: z
        .object({
        firstApproverId: Ulid.nullable(),
        firstApprovedAt: IsoDateTime.nullable(),
        approvedBy: Ulid.nullable(),
    })
        .nullable(),
    legalHoldRef: z.string().nullable(),
    deadlineExtendedTo: IsoDateTime.nullable(),
    highValueThreshold: MoneyDto,
});
export const UpdatePayoutDestinationRequest = z.object({
    destination: PayoutDestinationInput,
    reason: z.string().trim().min(5).max(300),
});
export const LegalHoldRequest = z.object({
    enabled: z.boolean(),
    reference: z.string().trim().min(3).max(120),
    reason: z.string().trim().min(5).max(500),
});
export const ClaimEvidenceAccessRequest = z.object({
    /** Written to the audit trail (§11.4 "only privileged reviewers access sensitive evidence"). */
    purpose: z.string().trim().min(10).max(300),
});
export const ClaimEvidenceAccessResponse = z.object({
    url: z.string(),
    expiresAt: IsoDateTime,
    watermarked: z.boolean(),
    auditId: Ulid,
});
export const AppealDecisionRequest = z.object({
    decision: z.enum(['REOPEN', 'UPHOLD']),
    note: z.string().trim().min(3).max(2000),
});
// --- fulfillment console (§16.2) --------------------------------------------------------------------------
export const FulfillmentListQuery = z.object({
    status: FulfillmentStatus.optional(),
    type: FulfillmentType.optional(),
    owner: z.string().max(120).optional(),
    overdue: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .optional(),
    cursor: z.string().max(256).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
});
export const StartFulfillmentRequest = z.object({
    carrier: z.string().max(80).optional(),
    trackingRef: z.string().max(120).optional(),
    pickupInstructions: z.string().max(500).optional(),
    note: z.string().max(1000).optional(),
});
export const FulfillmentExceptionRequest = z.object({
    stage: z.string().trim().min(2).max(80),
    /** Participant-safe note; rendered through copy.fulfillment_exception. */
    note: z.string().trim().min(3).max(500),
    nextUpdateAt: IsoDateTime,
    owner: z.string().max(120).optional(),
});
export const CompleteFulfillmentRequest = z.object({
    /** Storage/export reference of the delivery evidence (signed pickup, carrier POD, transfer receipt). */
    evidenceRef: z.string().trim().min(1).max(512),
    completionRef: z.string().max(200).optional(),
    note: z.string().max(1000).optional(),
});
/** Verification webhook acknowledgement (`POST /v1/claims/verification/webhook`). */
export const VerificationWebhookAckDto = z.object({
    received: z.number().int(),
    applied: z.number().int(),
    ignored: z.number().int(),
});
//# sourceMappingURL=claims.js.map